import React, { useState, useMemo, useEffect } from 'react';
import { Committee } from '../api/models/models';
import { Subcommittee } from '../api/models/models';
import { Politician } from '../api/models/models';
import { ChevronDownIcon, ChevronRightIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { congressionalApi, setTokenProvider } from '../services/congressionalApiService';
import { useAppSelector } from '../store/hooks';

interface CommitteeOverviewComponentProps {
    onCommitteeSelect?: (committee: Committee) => void;
    onPoliticianSelect?: (politician: Politician) => void;
    onSubcommitteeSelect?: (subcommittee: Subcommittee) => void;
    onNavigateToSubcommittees?: (committeeId?: string) => void;
}

const CommitteeOverviewComponent: React.FC<CommitteeOverviewComponentProps> = ({
    onCommitteeSelect,
    onPoliticianSelect,
    onSubcommitteeSelect,
    onNavigateToSubcommittees
}) => {
    const [committees, setCommittees] = useState<Committee[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState<'name' | 'members' | 'subcommittees'>('name');
    const [expandedCommittees, setExpandedCommittees] = useState<Set<string>>(new Set());
    const [expandedSubcommittees, setExpandedSubcommittees] = useState<Set<string>>(new Set());
    
    const credentials = useAppSelector((state) => state.user.credentials);

    // Set up token provider
    useEffect(() => {
        setTokenProvider({
            getAccessToken: () => credentials?.accessToken || null
        });
    }, [credentials]);

    // Fetch committees data
    useEffect(() => {
        const fetchCommittees = async () => {
            if (!credentials?.accessToken) {
                setError('No access token available');
                setLoading(false);
                return;
            }
            
            try {
                setLoading(true);
                const committeesData = await congressionalApi.getCommittees();
                setCommittees(committeesData);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to fetch committees');
                console.error('Error fetching committees:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchCommittees();
    }, [credentials?.accessToken]);

    // Filter and sort committees based on search and sort criteria
    const filteredAndSortedCommittees = useMemo(() => {
        const filtered = committees.filter(committee =>
            committee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            committee.members?.some(member => 
                member.politician?.name.toLowerCase().includes(searchTerm.toLowerCase())
            ) ||
            committee.subcommittees?.some(sub =>
                sub.name.toLowerCase().includes(searchTerm.toLowerCase())
            )
        );

        return filtered.sort((a, b) => {
            switch (sortBy) {
                case 'name':
                    return a.name.localeCompare(b.name);
                case 'members':
                    const aTotalMembers = (a.members?.length || 0) + 
                        (a.subcommittees?.reduce((sum, sub) => sum + (sub.members?.length || 0), 0) || 0);
                    const bTotalMembers = (b.members?.length || 0) + 
                        (b.subcommittees?.reduce((sum, sub) => sum + (sub.members?.length || 0), 0) || 0);
                    return bTotalMembers - aTotalMembers;
                case 'subcommittees':
                    return (b.subcommittees?.length || 0) - (a.subcommittees?.length || 0);
                default:
                    return 0;
            }
        });
    }, [committees, searchTerm, sortBy]);

    const toggleCommitteeExpansion = (committeeId: string) => {
        const newExpanded = new Set(expandedCommittees);
        if (newExpanded.has(committeeId)) {
            newExpanded.delete(committeeId);
        } else {
            newExpanded.add(committeeId);
        }
        setExpandedCommittees(newExpanded);
    };

    const toggleSubcommitteeExpansion = (subcommitteeId: string) => {
        const newExpanded = new Set(expandedSubcommittees);
        if (newExpanded.has(subcommitteeId)) {
            newExpanded.delete(subcommitteeId);
        } else {
            newExpanded.add(subcommitteeId);
        }
        setExpandedSubcommittees(newExpanded);
    };

    const getPartyColor = (party: string) => {
        if (party.toLowerCase().includes('democrat')) return 'text-blue-600';
        if (party.toLowerCase().includes('republican')) return 'text-red-600';
        return 'text-gray-600';
    };

    const getPartyBadgeColor = (party: string) => {
        if (party.toLowerCase().includes('democrat')) return 'bg-blue-100 text-blue-800';
        if (party.toLowerCase().includes('republican')) return 'bg-red-100 text-red-800';
        return 'bg-gray-100 text-gray-800';
    };

    const renderPolitician = (politician: Politician, isSubcommittee = false) => (
        <div
            key={politician.id}
            className={`flex items-center justify-between p-3 rounded-lg border border-neutral-200 cursor-pointer hover:bg-neutral-50 hover:border-primary-300 transition-colors ${
                isSubcommittee ? 'ml-6' : ''
            }`}
            onClick={() => onPoliticianSelect?.(politician)}
        >
            <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-primary-400 to-primary-600 rounded-full flex items-center justify-center shadow-sm">
                    <span className="text-sm font-semibold text-white">
                        {politician.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </span>
                </div>
                <div>
                    <p className="font-semibold text-neutral-900">{politician.name}</p>
                    <p className="text-sm text-neutral-600">{politician.state} - {politician.district}</p>
                </div>
            </div>
            <div className="flex items-center space-x-2">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPartyBadgeColor(politician.party)}`}>
                    {politician.party.charAt(0)}
                </span>
            </div>
        </div>
    );

    const renderSubcommittee = (subcommittee: Subcommittee) => {
        const isExpanded = expandedSubcommittees.has(subcommittee.id);
        const hasMembers = subcommittee.members && subcommittee.members.length > 0;
        const memberCount = subcommittee.members?.length || 0;

        return (
            <div key={subcommittee.id} className="border border-neutral-200 rounded-lg mb-3 ml-4 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                <div 
                    className="flex items-center justify-between p-3 cursor-pointer hover:bg-neutral-50 bg-white"
                    onClick={() => {
                        if (hasMembers) toggleSubcommitteeExpansion(subcommittee.id);
                        onSubcommitteeSelect?.(subcommittee);
                    }}
                >
                    <div className="flex items-center space-x-3">
                        <div className="flex items-center space-x-2">
                            {hasMembers ? (
                                isExpanded ? (
                                    <ChevronDownIcon className="w-4 h-4 text-neutral-400" />
                                ) : (
                                    <ChevronRightIcon className="w-4 h-4 text-neutral-400" />
                                )
                            ) : (
                                <div className="w-4 h-4" />
                            )}
                            <UserGroupIcon className="w-5 h-5 text-secondary-600" />
                        </div>
                        <div>
                            <h4 className="font-semibold text-neutral-900">{subcommittee.name}</h4>
                        </div>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                        <div className="text-center">
                            <div className="text-lg font-bold text-secondary-600">{memberCount}</div>
                            <div className="text-xs text-neutral-500">members</div>
                        </div>
                        
                        <div className="flex flex-col space-y-1">
                            {subcommittee.link && (
                                <a
                                    href={subcommittee.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary-600 hover:text-primary-800 text-xs font-medium"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    View Details →
                                </a>
                            )}
                        </div>
                    </div>
                </div>

                {isExpanded && hasMembers && (
                    <div className="border-t border-neutral-200 bg-neutral-50 p-3">
                        <h5 className="font-medium text-neutral-700 mb-3 flex items-center text-sm">
                            <UserGroupIcon className="w-4 h-4 mr-2" />
                            Subcommittee Members ({memberCount})
                        </h5>
                        <div className="grid gap-2">
                            {subcommittee.members?.map(member => 
                                member.politician && renderPolitician(member.politician, true)
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const renderCommittee = (committee: Committee) => {
        const isExpanded = expandedCommittees.has(committee.id);
        const totalMembers = (committee.members?.length || 0) + 
            (committee.subcommittees?.reduce((sum, sub) => sum + (sub.members?.length || 0), 0) || 0);
        const subcommitteeCount = committee.subcommittees?.length || 0;

        return (
            <div key={committee.id} className="border border-neutral-200 rounded-lg mb-4 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                <div 
                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-neutral-50 bg-white"
                    onClick={() => {
                        toggleCommitteeExpansion(committee.id);
                        onCommitteeSelect?.(committee);
                    }}
                >
                    <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2">
                            {isExpanded ? (
                                <ChevronDownIcon className="w-5 h-5 text-neutral-400" />
                            ) : (
                                <ChevronRightIcon className="w-5 h-5 text-neutral-400" />
                            )}
                            <UserGroupIcon className="w-6 h-6 text-primary-600" />
                        </div>
                        <div>
                            <h3 className="font-semibold text-neutral-900 text-lg">{committee.name}</h3>
                            <p className="text-sm text-neutral-600 mt-1">
                                {committee.members?.length || 0} direct members • {totalMembers} total members
                            </p>
                        </div>
                    </div>
                    
                    <div className="flex items-center space-x-4">
                        <div className="text-center min-w-[80px]">
                            <div className="text-2xl font-bold text-primary-600">{subcommitteeCount}</div>
                            <div className="text-xs text-neutral-500">subcommittees</div>
                        </div>
                        
                        <div className="flex flex-col space-y-2 min-w-[140px]">
                            {committee.link && (
                                <a
                                    href={committee.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    View Details →
                                </a>
                            )}
                            {subcommitteeCount > 0 && onNavigateToSubcommittees ? (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onNavigateToSubcommittees(committee.id);
                                    }}
                                    className="text-secondary-600 hover:text-secondary-800 text-sm font-medium text-left"
                                >
                                    View Subcommittees →
                                </button>
                            ) : (
                                <div className="h-5" />
                            )}
                        </div>
                    </div>
                </div>

                {isExpanded && (
                    <div className="border-t border-neutral-200">
                        {/* Direct Committee Members */}
                        {committee.members && committee.members.length > 0 && (
                            <div className="p-4 bg-neutral-50">
                                <h4 className="font-semibold text-neutral-700 mb-3 flex items-center">
                                    <UserGroupIcon className="w-4 h-4 mr-2" />
                                    Committee Members ({committee.members.length})
                                </h4>
                                <div className="grid gap-3">
                                    {committee.members.map(member => 
                                        member.politician && renderPolitician(member.politician)
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Subcommittees */}
                        {committee.subcommittees && committee.subcommittees.length > 0 && (
                            <div className="p-4 border-t border-neutral-200 bg-white">
                                <div className="flex items-center justify-between mb-3">
                                    <h4 className="font-semibold text-neutral-700 flex items-center">
                                        <UserGroupIcon className="w-4 h-4 mr-2" />
                                        Subcommittees ({subcommitteeCount})
                                    </h4>
                                    {onNavigateToSubcommittees && (
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onNavigateToSubcommittees(committee.id);
                                            }}
                                            className="text-secondary-600 hover:text-secondary-800 text-sm font-medium"
                                        >
                                            View All Subcommittees →
                                        </button>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    {committee.subcommittees.map(subcommittee => 
                                        renderSubcommittee(subcommittee)
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    // Loading state
    if (loading) {
        return (
            <div className="bg-white shadow-lg rounded-lg border border-neutral-300 p-6">
                <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                        <p className="mt-4 text-neutral-600">Loading committees...</p>
                    </div>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="bg-white shadow-lg rounded-lg border border-neutral-300 p-6">
                <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                        <div className="text-red-600 text-xl mb-2">⚠️</div>
                        <p className="text-red-600 font-semibold">Error loading committees</p>
                        <p className="text-neutral-600 text-sm mt-2">{error}</p>
                        <button 
                            onClick={() => window.location.reload()} 
                            className="mt-4 px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
                        >
                            Retry
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white shadow-lg rounded-lg border border-neutral-300 p-6">
            <div className="mb-6">
                <h2 className="text-2xl font-semibold text-primary-900 mb-4">
                    Congressional Committees ({filteredAndSortedCommittees.length})
                </h2>

                <div className="flex flex-col sm:flex-row gap-4 mb-4">
                    {/* Search */}
                    <div className="flex-1">
                        <input
                            type="text"
                            placeholder="Search committees, subcommittees, or members..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                    </div>

                    {/* Sort */}
                    <div className="sm:w-48">
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                            className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        >
                            <option value="name">Sort by Name</option>
                            <option value="members">Sort by Total Members</option>
                            <option value="subcommittees">Sort by Subcommittees</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Summary Stats */}
            <div className="mt-8 p-4 bg-gradient-to-r from-primary-50 to-secondary-50 rounded-lg border border-primary-200">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-center">
                    <div className="bg-white p-3 rounded-lg shadow-sm">
                        <p className="text-2xl font-bold text-primary-600">{committees.length}</p>
                        <p className="text-sm text-neutral-600">Total Committees</p>
                    </div>
                    <div className="bg-white p-3 rounded-lg shadow-sm">
                        <p className="text-2xl font-bold text-secondary-600">
                            {committees.reduce((sum, c) => sum + (c.subcommittees?.length || 0), 0)}
                        </p>
                        <p className="text-sm text-neutral-600">Total Subcommittees</p>
                    </div>
                    <div className="bg-white p-3 rounded-lg shadow-sm">
                        <p className="text-2xl font-bold text-primary-600">
                            {committees.reduce((sum, c) => sum + (c.members?.length || 0), 0)}
                        </p>
                        <p className="text-sm text-neutral-600">Direct Members</p>
                    </div>
                    <div className="bg-white p-3 rounded-lg shadow-sm">
                        <p className="text-2xl font-bold text-neutral-600">
                            {Math.round(committees.reduce((sum, c) => sum + (c.members?.length || 0), 0) / Math.max(committees.length, 1))}
                        </p>
                        <p className="text-sm text-neutral-600">Avg Members</p>
                    </div>
                </div>
            </div>

            {/* Quick Actions */}
            <div className="mt-6 flex flex-wrap gap-2 mb-6">
                <button
                    onClick={() => setExpandedCommittees(new Set(committees.map(c => c.id)))}
                    className="px-4 py-2 text-sm bg-primary-100 text-primary-700 rounded-md hover:bg-primary-200 transition-colors"
                >
                    Expand All
                </button>
                <button
                    onClick={() => setExpandedCommittees(new Set())}
                    className="px-4 py-2 text-sm bg-neutral-100 text-neutral-700 rounded-md hover:bg-neutral-200 transition-colors"
                >
                    Collapse All
                </button>
                <button
                    onClick={() => setSortBy('members')}
                    className="px-4 py-2 text-sm bg-secondary-100 text-secondary-700 rounded-md hover:bg-secondary-200 transition-colors"
                >
                    Show Largest First
                </button>
            </div>

            {/* Committees List */}
            <div className="space-y-4">
                {filteredAndSortedCommittees.map(committee => renderCommittee(committee))}
            </div>

            {filteredAndSortedCommittees.length === 0 && (
                <div className="text-center py-8 text-neutral-500">
                    No committees found matching your search.
                </div>
            )}

        </div>
    );
};

export default CommitteeOverviewComponent;
